import React, { useState, useRef, useEffect, useCallback } from 'react';

const OptimizedImage = ({ 
  src, 
  alt, 
  className = '', 
  loading = 'lazy',
  width,
  height,
  sizes = '(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw',
  priority = false,
  placeholder = true,
  style = {}
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const imgRef = useRef(null);
  const [currentSrc, setCurrentSrc] = useState(null);

  // Simplified and more reliable URL generation
  const getOptimizedImageUrl = useCallback((originalUrl, format = 'auto', targetWidth = 800) => {
    if (!originalUrl) return originalUrl;
    
    try {
      // For Unsplash images - use their API for optimization
      if (originalUrl.includes('unsplash.com')) {
        const baseUrl = originalUrl.split('?')[0];
        // Use a more conservative approach with Unsplash's format parameter
        if (format === 'webp') {
          return `${baseUrl}?w=${targetWidth}&q=80&fm=webp&fit=crop&auto=format`;
        }
        return `${baseUrl}?w=${targetWidth}&q=85&fit=crop&auto=format`;
      }
      
      // For Pexels images - return as-is since they handle optimization
      if (originalUrl.includes('pexels.com')) {
        return originalUrl;
      }
      
      // For other URLs, return as-is
      return originalUrl;
    } catch (error) {
      console.warn('URL optimization failed, using original:', error);
      return originalUrl;
    }
  }, []);

  // Generate fallback URLs with progressive degradation
  const generateFallbackUrls = useCallback((originalUrl) => {
    const baseWidth = width || 800;
    return [
      getOptimizedImageUrl(originalUrl, 'webp', baseWidth),
      getOptimizedImageUrl(originalUrl, 'auto', baseWidth),
      originalUrl // Ultimate fallback
    ].filter(Boolean);
  }, [width, getOptimizedImageUrl]);

  const handleLoad = useCallback(() => {
    setImageLoaded(true);
    setImageError(false);
  }, []);

  const handleError = useCallback(() => {
    console.warn('Image load error for:', currentSrc);
    
    // Try fallback URLs
    const fallbackUrls = generateFallbackUrls(src);
    const nextUrlIndex = retryCount + 1;
    
    if (nextUrlIndex < fallbackUrls.length && retryCount < 2) {
      console.log(`Retrying with fallback URL ${nextUrlIndex}:`, fallbackUrls[nextUrlIndex]);
      setRetryCount(nextUrlIndex);
      setCurrentSrc(fallbackUrls[nextUrlIndex]);
      setImageError(false);
    } else {
      console.error('All image loading attempts failed for:', src);
      setImageError(true);
    }
  }, [currentSrc, retryCount, src, generateFallbackUrls]);

  // Initialize image source
  useEffect(() => {
    if (src && !currentSrc) {
      const fallbackUrls = generateFallbackUrls(src);
      setCurrentSrc(fallbackUrls[0]);
    }
  }, [src, currentSrc, generateFallbackUrls]);

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (loading === 'lazy' && imgRef.current && currentSrc) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const img = entry.target;
              if (img.dataset.src && !img.src) {
                img.src = img.dataset.src;
                if (img.dataset.srcset) {
                  img.srcset = img.dataset.srcset;
                }
              }
              observer.unobserve(img);
            }
          });
        },
        { 
          rootMargin: '50px 0px',
          threshold: 0.1
        }
      );
      
      observer.observe(imgRef.current);
      return () => observer.disconnect();
    }
  }, [loading, currentSrc]);

  const imageStyle = {
    transition: 'opacity 0.3s ease',
    opacity: imageLoaded ? 1 : 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    ...style
  };

  const placeholderStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: 'linear-gradient(135deg, rgba(167, 101, 69, 0.1) 0%, rgba(255, 165, 93, 0.1) 100%)',
    opacity: imageLoaded ? 0 : 1,
    transition: 'opacity 0.3s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'rgba(167, 101, 69, 0.6)',
    fontSize: '14px',
    fontWeight: '500'
  };

  // Error state with better styling
  if (imageError) {
    return (
      <div 
        className={className} 
        style={{ 
          background: 'linear-gradient(135deg, rgba(167, 101, 69, 0.05) 0%, rgba(255, 165, 93, 0.05) 100%)',
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          color: 'rgba(167, 101, 69, 0.6)',
          fontSize: '14px',
          fontWeight: '500',
          minHeight: height || '200px',
          ...style
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <div style={{ marginBottom: '8px' }}>📷</div>
          <div>Image not available</div>
        </div>
      </div>
    );
  }

  // Don't render anything if no source is available
  if (!currentSrc) {
    return (
      <div className={className} style={{ ...placeholderStyle, position: 'relative' }}>
        Loading...
      </div>
    );
  }

  return (
    <div className={className} style={{ overflow: 'hidden', ...style }}>
      {placeholder && !imageLoaded && (
        <div style={placeholderStyle}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ marginBottom: '8px' }}>⏳</div>
            <div>Loading...</div>
          </div>
        </div>
      )}
      
      <img
        ref={imgRef}
        src={loading === 'lazy' ? undefined : currentSrc}
        data-src={loading === 'lazy' ? currentSrc : undefined}
        alt={alt}
        loading={priority ? 'eager' : loading}
        style={imageStyle}
        onLoad={handleLoad}
        onError={handleError}
        sizes={sizes}
        width={width}
        height={height}
        decoding="async"
      />
    </div>
  );
};

export default OptimizedImage;